import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Skeleton,
  Tabs,
  Tab,
  Chip,
  Tooltip,
  IconButton,
  alpha,
  Divider,
  useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { 
  Target, 
  TrendingUp, 
  TrendingDown, 
  Zap,
  Clock,
  BarChart3,
  ArrowRight,
  CheckCircle,
  XCircle,
  Activity,
  Calendar,
  Award,
  TrendingDown as Flame,
} from 'lucide-react';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useNavigate } from 'react-router-dom';

import { taskService } from '@/services/api';
import { formatPercent } from '@/shared/utils/formatPercentage';

// Extend dayjs with plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// Period type for performance tracking
type PerformancePeriod = 'day' | 'week' | 'month' | 'year' | 'history';

interface PerformanceData {
  completed: number;
  missed: number;
  pending: number;
  total: number;
  hitRate: number;
  estimatedMinutes: number;
  actualMinutes: number;
  velocity: number; // actual/estimated
}

interface DailyStat {
  date: string;
  totalPicked: number;
  totalHits: number;
  totalMisses: number;
  totalPending?: number;
  totalEstimatedMinutes: number;
  totalActualMinutes: number | null;
  hitRate: number | null;
  evaluated: boolean;
}

/**
 * DailyPerformanceWidget
 * Shows comprehensive performance tracking with hit/miss metrics based on daily task commitments
 * Tracks: Daily, Weekly, Monthly, Yearly performance with actual task picking data
 */
const DailyPerformanceWidget: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [period, setPeriod] = useState<PerformancePeriod>('day');

  // Calculate days needed for fetching performance summary
  const getDaysForPeriod = () => {
    switch (period) {
      case 'day':
        return 1;
      case 'week':
        return 7;
      case 'month':
        return 30;
      case 'year':
        return 365;
      case 'history':
        return 365;
      default:
        return 30;
    }
  };

  // Fetch performance summary for all periods
  const { data: performanceSummaryData, isLoading, isFetching, error } = useQuery({
    queryKey: ['performance-summary', getDaysForPeriod()],
    queryFn: () => taskService.getPerformanceSummary(getDaysForPeriod()),
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

  // Calculate performance for selected period from commitment data
  const calculatePerformanceFromCommitments = (periodType: PerformancePeriod): PerformanceData => {
    if (!performanceSummaryData || !performanceSummaryData.dailyStats || !Array.isArray(performanceSummaryData.dailyStats)) {
      return {
        completed: 0,
        missed: 0,
        pending: 0,
        total: 0,
        hitRate: 0,
        estimatedMinutes: 0,
        actualMinutes: 0,
        velocity: 0,
      };
    }

    const now = dayjs();
    let startDate: dayjs.Dayjs;
    let endDate: dayjs.Dayjs = now.endOf('day');

    switch (periodType) {
      case 'day':
        startDate = now.startOf('day');
        break;
      case 'week':
        startDate = now.startOf('week');
        break;
      case 'month':
        startDate = now.startOf('month');
        break;
      case 'year':
        startDate = now.startOf('year');
        break;
      default:
        startDate = now.startOf('day');
    }

    // Filter daily stats for the selected period
    const periodStats = (performanceSummaryData.dailyStats as DailyStat[]).filter((day: DailyStat) => {
      const dayDate = dayjs(day.date);
      return dayDate.isSameOrAfter(startDate, 'day') && dayDate.isSameOrBefore(endDate, 'day');
    });

    // Aggregate the data
    const completed = periodStats.reduce((sum, day) => sum + (day.totalHits || 0), 0);
    const missed = periodStats.reduce((sum, day) => sum + (day.totalMisses || 0), 0);
    const pending = periodStats.reduce((sum, day) => sum + (day.totalPending || 0), 0);
    const total = periodStats.reduce((sum, day) => sum + (day.totalPicked || 0), 0);
    const hitRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    // Calculate time-based metrics (convert minutes to hours for display)
    const estimatedMinutes = periodStats.reduce((sum, day) => sum + (day.totalEstimatedMinutes || 0), 0);
    const actualMinutes = periodStats.reduce((sum, day) => sum + (day.totalActualMinutes || 0), 0);
    
    // Velocity: actual/estimated (< 1 means faster, > 1 means slower)
    const velocity = estimatedMinutes > 0 ? actualMinutes / estimatedMinutes : 0;

    return {
      completed,
      missed,
      pending,
      total,
      hitRate,
      estimatedMinutes: estimatedMinutes / 60, // Convert to hours for display
      actualMinutes: actualMinutes / 60, // Convert to hours for display
      velocity,
    };
  };

  // Period labels
  const periodLabels: Record<PerformancePeriod, string> = {
    day: 'Today',
    week: 'This Week',
    month: 'This Month',
    year: 'This Year',
    history: 'Performance History',
  };

  // Show loading skeleton during initial load or when fetching new period data
  if (isLoading || isFetching) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width={180} height={28} />
          <Skeleton variant="rounded" height={40} sx={{ mt: 2 }} />
          <Skeleton variant="rounded" height={100} sx={{ mt: 2 }} />
          <Skeleton variant="text" width={100} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  // Show error state
  if (error) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <XCircle size={32} style={{ color: '#ef4444', marginBottom: 8 }} />
            <Typography variant="body2" color="error">
              Failed to load performance data
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Please try again later
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Calculate performance AFTER loading checks
  const performance = calculatePerformanceFromCommitments(period);

  // Determine status color
  const getStatusColor = (rate: number) => {
    if (rate >= 80) return 'success';
    if (rate >= 60) return 'warning';
    return 'error';
  };

  // Determine velocity status (good if velocity < 1, meaning faster than estimated)
  const getVelocityStatus = (velocity: number) => {
    if (velocity === 0) return { color: theme.palette.text.secondary, label: 'No data', icon: Activity };
    if (velocity <= 0.8) return { color: theme.palette.success.main, label: 'Excellent', icon: TrendingUp };
    if (velocity <= 1.2) return { color: theme.palette.warning.main, label: 'On track', icon: Activity };
    return { color: theme.palette.error.main, label: 'Behind', icon: TrendingDown };
  };

  const statusColor = getStatusColor(performance.hitRate);
  const velocityStatus = getVelocityStatus(performance.velocity);
  const VelocityIcon = velocityStatus.icon;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Target size={20} style={{ color: '#64748b' }} />
            <Typography variant="h6" fontWeight={600}>
              Performance Tracker
            </Typography>
          </Box>
          <Tooltip title="View detailed performance">
            <IconButton size="small" onClick={() => navigate('/tasks')}>
              <ArrowRight size={18} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Period Tabs */}
        <Tabs
          value={period}
          onChange={(_, newValue) => setPeriod(newValue)}
          sx={{
            minHeight: 36,
            mb: 2,
            '& .MuiTab-root': {
              minHeight: 36,
              py: 0.5,
              px: 2,
              fontSize: '0.75rem',
              fontWeight: 500,
            },
          }}
        >
          <Tab label="Day" value="day" />
          <Tab label="Week" value="week" />
          <Tab label="Month" value="month" />
          <Tab label="Year" value="year" />
          <Tab label="History" value="history" />
        </Tabs>

        {/* Conditional rendering based on tab */}
        {period === 'history' ? (
          // History Tab Content
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {performanceSummaryData && Array.isArray(performanceSummaryData.dailyStats) && performanceSummaryData.dailyStats.length > 0 ? (
              <>
                {/* Summary Stats */}
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(2, 1fr)', 
                  gap: 1.5, 
                  mb: 3 
                }}>
                  <Box sx={{ 
                    p: 1.5, 
                    bgcolor: 'success.50', 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'success.200'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Flame size={14} style={{ color: '#f97316' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Current Streak
                      </Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={700} color="success.main">
                      {performanceSummaryData.currentStreak}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      days
                    </Typography>
                  </Box>

                  <Box sx={{ 
                    p: 1.5, 
                    bgcolor: 'warning.50', 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'warning.200'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Award size={14} style={{ color: '#eab308' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Best Streak
                      </Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={700} color="warning.main">
                      {performanceSummaryData.bestStreak}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      days
                    </Typography>
                  </Box>

                  <Box sx={{ 
                    p: 1.5, 
                    bgcolor: 'primary.50', 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'primary.200'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Target size={14} style={{ color: '#4F46E5' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Avg Hit Rate
                      </Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={700} color="primary.main">
                      {formatPercent(performanceSummaryData.averageHitRate)}
                    </Typography>
                  </Box>

                  <Box sx={{ 
                    p: 1.5, 
                    bgcolor: 'grey.50', 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <BarChart3 size={14} style={{ color: '#6b7280' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Total Picks
                      </Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={700}>
                      {performanceSummaryData.totalPicks}
                    </Typography>
                  </Box>
                </Box>

                {/* Calendar Heatmap */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                    <Calendar size={14} style={{ color: '#6b7280' }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Last 30 Days
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(10, 1fr)', 
                    gap: 0.5 
                  }}>
                    {performanceSummaryData.dailyStats.slice().reverse().map((day: DailyStat, index: number) => {
                      const hitRate = day.totalPicked > 0 ? (day.totalHits / day.totalPicked) * 100 : 0;
                      const getColor = () => {
                        if (day.totalPicked === 0) return theme.palette.grey[100];
                        if (hitRate >= 80) return theme.palette.success.main;
                        if (hitRate >= 60) return theme.palette.warning.main;
                        return theme.palette.error.main;
                      };
                      
                      return (
                        <Tooltip
                          key={index}
                          title={`${dayjs(day.date).format('MMM D')}: ${day.totalHits}/${day.totalPicked} hits${day.totalMisses > 0 ? `, ${day.totalMisses} misses` : ''}${(day.totalPending ?? 0) > 0 ? `, ${day.totalPending} pending` : ''} (${formatPercent(Number(day.hitRate ?? 0))})`}
                        >
                          <Box
                            sx={{
                              aspectRatio: '1',
                              bgcolor: getColor(),
                              borderRadius: 0.5,
                              border: '1px solid',
                              borderColor: 'grey.200',
                              opacity: day.totalPicked === 0 ? 0.3 : 1,
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: 'grey.100', borderRadius: 0.5 }} />
                      <Typography variant="caption" color="text.secondary">No tasks</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: 'error.main', borderRadius: 0.5 }} />
                      <Typography variant="caption" color="text.secondary">&lt;60%</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: 'warning.main', borderRadius: 0.5 }} />
                      <Typography variant="caption" color="text.secondary">60-80%</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: 'success.main', borderRadius: 0.5 }} />
                      <Typography variant="caption" color="text.secondary">≥80%</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Recent 7 Days List */}
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', mb: 1 }}>
                    Last 7 Days
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {performanceSummaryData.dailyStats.slice(0, 7).map((day: DailyStat, index: number) => {
                      const hitRate = day.totalPicked > 0 ? (day.totalHits / day.totalPicked) * 100 : 0;
                      const statusColor = hitRate >= 80 ? 'success' : hitRate >= 60 ? 'warning' : 'error';
                      
                      return (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.200',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Calendar size={14} style={{ color: '#6b7280' }} />
                            <Typography variant="body2" fontWeight={500}>
                              {dayjs(day.date).format('MMM D, ddd')}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {day.totalPicked > 0 ? (
                              <>
                                <Chip
                                  size="small"
                                  label={`${day.totalHits}/${day.totalPicked}`}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    bgcolor: alpha(theme.palette[statusColor].main, 0.1),
                                    color: `${statusColor}.main`,
                                    fontWeight: 600,
                                  }}
                                />
                                <Typography variant="caption" fontWeight={600} color={`${statusColor}.main`}>
                                  {formatPercent(hitRate)}
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                No tasks
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Calendar size={32} style={{ color: '#94a3b8', marginBottom: 8 }} />
                <Typography variant="body2" color="text.secondary">
                  No performance data yet
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Start picking tasks to build your history
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          // Regular Period Content (Day/Week/Month/Year)
          <>
        {/* Hit Rate Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              Hit Rate - {periodLabels[period]}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: `${statusColor}.main`,
              }}
            >
              {formatPercent(performance.hitRate)}
            </Typography>
          </Box>
          
          <LinearProgress
            variant="determinate"
            value={performance.hitRate}
            color={statusColor}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'grey.100',
              mb: 1,
            }}
          />

          {/* Task Stats */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircle size={14} style={{ color: '#22c55e' }} />
              <Typography variant="body2" color="text.secondary">
                <strong style={{ color: '#22c55e' }}>{performance.completed}</strong> Done
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <XCircle size={14} style={{ color: '#ef4444' }} />
              <Typography variant="body2" color="text.secondary">
                <strong style={{ color: '#ef4444' }}>{performance.missed}</strong> Missed
              </Typography>
            </Box>
            {performance.pending > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Clock size={14} style={{ color: '#f59e0b' }} />
                <Typography variant="body2" color="text.secondary">
                  <strong style={{ color: '#f59e0b' }}>{performance.pending}</strong> Pending
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <BarChart3 size={14} style={{ color: '#6b7280' }} />
              <Typography variant="body2" color="text.secondary">
                <strong>{performance.total}</strong> Total
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Velocity Section */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Zap size={16} style={{ color: '#f59e0b' }} />
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Velocity
              </Typography>
            </Box>
            <Chip
              icon={<VelocityIcon size={12} />}
              label={velocityStatus.label}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: alpha(velocityStatus.color, 0.1),
                color: velocityStatus.color,
                fontWeight: 600,
                '& .MuiChip-icon': { color: velocityStatus.color },
              }}
            />
          </Box>

          {/* Time Stats */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: 1.5, 
            p: 1.5, 
            bgcolor: 'grey.50', 
            borderRadius: 2 
          }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Planned
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Clock size={14} style={{ color: '#4F46E5' }} />
                <Typography variant="body2" fontWeight={600}>
                  {performance.estimatedMinutes.toFixed(1)}h
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Actual
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Clock size={14} style={{ color: '#22c55e' }} />
                <Typography variant="body2" fontWeight={600}>
                  {performance.actualMinutes.toFixed(1)}h
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Velocity Ratio */}
          {performance.velocity > 0 && (
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Velocity Ratio: 
                <strong style={{ 
                  color: performance.velocity <= 1 ? '#22c55e' : '#f59e0b',
                  marginLeft: 4
                }}>
                  {performance.velocity.toFixed(2)}x
                </strong>
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem', mt: 0.5 }}>
                {performance.velocity <= 1 
                  ? `${formatPercent((1 - performance.velocity) * 100)} faster than estimated`
                  : `${formatPercent((performance.velocity - 1) * 100)} slower than estimated`
                }
              </Typography>
            </Box>
          )}
        </Box>

        {/* Motivational Message */}
        <Box
          sx={{
            mt: 'auto',
            pt: 2,
            px: 1.5,
            py: 1,
            bgcolor: alpha(
              statusColor === 'success' 
                ? theme.palette.success.main
                : statusColor === 'warning' 
                ? theme.palette.warning.main 
                : theme.palette.error.main, 
              0.08
            ),
            borderRadius: 1.5,
            textAlign: 'center',
            borderLeft: 3,
            borderColor: `${statusColor}.main`,
          }}
        >
          <Typography variant="caption" sx={{ color: `${statusColor}.700`, fontWeight: 500, display: 'block' }}>
            {performance.hitRate >= 80
              ? "🎯 Outstanding! Keep up the excellent work!"
              : performance.hitRate >= 60
              ? "💪 Good progress! Push for that 80%!"
              : performance.total === 0
              ? "Start picking tasks to see your performance"
              : performance.pending > 0
              ? `${performance.pending} task(s) still in progress — misses finalize at midnight`
              : "Time to accelerate! You've got this!"}
          </Typography>
        </Box>
          </>
        )}
        {/* End of conditional rendering */}
      </CardContent>
    </Card>
  );
};

export default DailyPerformanceWidget;
